import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { api } from "../api";
import { getBaseUrlParams } from "../../../../utils/getBaseUrlParams";
import { showToast } from "../../../../utils/showToast";

export const SET_LOADING = "saga/revenueCategories/SET_LOADING";

export const FETCH_REVENUE_CATEGORIES_SUCCESS =
  "saga/revenueCategories/FETCH_REVENUE_CATEGORIES_SUCCESS";

export const FETCH_REVENUE_CATEGORY = "saga/revenueCategories/FETCH_REVENUE_CATEGORY";
export const FETCH_REVENUE_CATEGORY_SUCCESS =
  "saga/revenueCategories/FETCH_REVENUE_CATEGORY_SUCCESS";

export const FETCH_FILTERS = "saga/revenueCategories/FETCH_FILTERS";
export const FETCH_ACTIONS = "saga/revenueCategories/FETCH_ACTIONS";

export const FETCH_CURRENT_USER = "saga/revenueCategories/FETCH_CURRENT_USER";
export const FETCH_CATEGORIES = "saga/revenueCategories/FETCH_REVENUE_CATEGORIES";

export const RESET_CURRENT_CATEGORY_INFO = "saga/revenueCategories/RESET_CURRENT_CATEGORY_INFO";
export const SET_ERROR = "saga/revenueCategories/SET_ERROR";
export const SET_STATUS = "saga/revenueCategories/SET_STATUS";
export const RESET_STATUS = "saga/revenueCategories/RESET_STATUS";

export const TOGGLE_CHALLENGERS = "saga/revenueCategories/TOGGLE_CHALLENGERS";
export const TOGGLE_CHALLENGERS_SUCCESS = "saga/revenueCategories/TOGGLE_CHALLENGERS_SUCCESS";

export const TOGGLE_TAGS = "saga/revenueCategories/TOGGLE_TAGS";
export const TOGGLE_TAGS_SUCCESS = "saga/revenueCategories/TOGGLE_TAGS_SUCCESS";

const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  fetchCategories: createAction(FETCH_CATEGORIES),
  getCategoryInfo: createAction(FETCH_REVENUE_CATEGORY),
  resetCategoryInfo: createAction(RESET_CURRENT_CATEGORY_INFO),
  resetFilters: createAction(RESET_STATUS),
  toggleChallengers: createAction(TOGGLE_CHALLENGERS),
  toggleTags: createAction(TOGGLE_TAGS)
};

export const {
  fetchCurrentUser,
  fetchCategories,
  getCategoryInfo,
  toggleChallengers,
  toggleTags,
  resetCategoryInfo,
  recalculate,
  upload
} = actionCreators;

export default function* watchRevenueCategories() {
  yield takeLatestSafe(FETCH_CURRENT_USER, getCurrentUserData);
  yield takeLatestSafe(FETCH_CATEGORIES, fetchRevenueCategories);
  yield takeLatestSafe(FETCH_REVENUE_CATEGORY, fetchRevenueCategory);
  yield takeLatestSafe(TOGGLE_CHALLENGERS, switchChallengersToggle);
  yield takeLatestSafe(TOGGLE_TAGS, switchTags);
}

function takeLatestSafe(action, effect) {
  return takeLatest(action, tryCatch(effect));
}

function tryCatch(effect) {
  return function* (args) {
    try {
      yield call(effect, args);
    } catch (err) {
      console.error(err);
      if (err.response.status === 422) {
        yield put({ type: SET_ERROR, payload: err.response.data });
      }
    }
  };
}

function* getCurrentUserData() {
  yield put({ type: SET_LOADING, payload: true });

  yield fetchRevenueCategories();
  yield fetchFilters();
  yield getActions();

  yield put({ type: SET_LOADING, payload: false });
}

function* fetchRevenueCategories({ payload: { key, data, show_inactive } = {} } = {}) {
  const { short_url } = getBaseUrlParams();
  const searchParam = key && data ? { [key]: data.id ? data.id : data } : {};

  const res = yield call(api.getRevenueCategories, {
    params: { short_url, ...searchParam, show_inactive }
  });

  yield put({
    type: FETCH_REVENUE_CATEGORIES_SUCCESS,
    payload: { data: res.data, searchParam: { ...searchParam, show_inactive } }
  });
}

function* fetchFilters() {
  const { short_url } = getBaseUrlParams();

  const { data } = yield call(api.getRevenueCategoriesFilters, { params: { short_url } });

  yield put({
    type: FETCH_FILTERS,
    payload: data
  });
}

function* getActions() {
  const { short_url } = getBaseUrlParams();

  const { data } = yield call(api.getActions, { params: { short_url } });

  yield put({
    type: FETCH_ACTIONS,
    payload: data
  });
}

function* switchChallengersToggle({ payload: { rcoId, challenger } }) {
  const { short_url } = getBaseUrlParams();

  let res = yield call(api.toggleChallengers, {
    id: rcoId.toString(),
    short_url,
    enable_challengers: !challenger
  });

  yield put({
    type: TOGGLE_CHALLENGERS_SUCCESS,
    payload: { rcoId, challenger: !challenger }
  });

  showToast(res.data.message);
}

function* switchTags({ payload: { revenueCategoryId, id, mass_toggle, ...rest } }) {
  const { short_url } = getBaseUrlParams();

  let tags = {};

  if (Object.hasOwn(rest, "offerRetention")) {
    tags.offer_retention = rest.offerRetention;
  } else {
    tags = rest;
  }

  yield call(api.toggleTags, {
    short_url,
    id: typeof id === "string" ? [id] : id,
    mass_toggle,
    revenue_category_id: revenueCategoryId.toString(),
    tags: tags
  });

  yield put({
    type: TOGGLE_TAGS_SUCCESS,
    payload: {
      id,
      tags: rest
    }
  });
}

function* fetchRevenueCategory({ payload: { id } }) {
  yield put({
    type: RESET_CURRENT_CATEGORY_INFO
  });

  const { short_url, organization_id } = getBaseUrlParams();
  const searchParam = yield select((state) => state.revenueCategories.searchParam);

  const { data } = yield call(api.getRevenueCategoryInfo, id, {
    params: { short_url, organization_id, ...searchParam }
  });

  yield put({
    type: FETCH_REVENUE_CATEGORY_SUCCESS,
    payload: { id, data }
  });
}
